import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PORTFOLIO_API_BASE, UNITS_API_BASE } from '../../../api/api';
import { deleteAPIRequest, useGenericAPI } from '../../../api/useGenericApi';
import { ProButton } from '../../../atoms/Button/ProButton';
import { ActionCell } from '../../../atoms/Cells/ActionCell';
import { ProIcon } from '../../../atoms/Icons/Icon';
import { ProIconData, ProIconDataButtons } from '../../../atoms/Icons/IconData';
import { ProTable, TableColData } from '../../../atoms/Table/Table';
import { DetaiWindowWrapper } from '../../../components/DetailWindow/DetailWindow';
import { ApplicationElementType, getBaseBCItemForType } from '../../../components/DetailWindow/breadCrump.helpers';
import { ProPage } from '../../../components/Page/Page';
import { UserContext } from '../../../contexts/UserContext';
import { ProNoDataOrRightsPage } from '../../../modules/ProErrorPage/NoDataOrRights';
import { useGenericDetailWindow } from '../../../modules/Windows/GenericDetailWindow';
import { UnitSummaryWindow } from '../../../modules/Windows/SummaryDetails/UnitSummaryWindow';
import { UnitDetailWindow } from '../../../modules/Windows/UnitDetailWindow/UnitDetailWindow';
import { ProIntent, ProSize } from '../../../utils/enums';
import { getHiddenForWindowDisplayState } from '../../../utils/helpers';
import { Components } from '../../../utils/types';

export const useUnitDetailWindow = ({
    closeAction,
    externalSync,
    unitId,
}: {
    closeAction: () => void;
    unitId?: string;
    externalSync?: () => void;
}) => {
    const { portfolio } = useContext(UserContext);
    return useGenericDetailWindow({
        apiConstant: UNITS_API_BASE,
        closeAction,
        emptyItem: getNewUnitDTO(portfolio?.endCustomerId),
        genericDetailWindow: UnitDetailWindow,
        externalSync,
        selectedItemId: unitId,
    });
};

export const getNewUnitDTO = (endCustomerId?: string): Components.Schemas.UnitDTO => ({
    name: 'Unnamed material',
    shortName: '-',
    endCustomerId: endCustomerId,
});

export const UnitsPage = () => {
    const { portfolio: initPortfolio } = useContext(UserContext);
    const [selectedTableRow, setSelectedTableRow] = useState<number>();
    const [selectedUnit, setSelectedUnit] = useState<Components.Schemas.UnitDTO>();
    const { t } = useTranslation();

    const deselectTableRow = () => {
        setSelectedUnit(undefined);
        setSelectedTableRow(undefined);
        openUnit('hidden');
    };

    const {
        data: portfolio,
        error,
        loading,
        sync,
    } = useGenericAPI<Components.Schemas.PortfolioDTO>(PORTFOLIO_API_BASE, initPortfolio?.id);

    const { units } = portfolio ?? {};

    const {
        isDisplayed: roleIsDisplayed,
        mode: unitMode,
        open: openUnit,
        renderDetailWindow: renderUnitDetailWindow,
    } = useUnitDetailWindow({ closeAction: deselectTableRow, externalSync: sync, unitId: selectedUnit?.id });

    const addUnit = () => {
        setSelectedUnit(undefined);
        openUnit('new');
        setSelectedTableRow(undefined);
    };

    if (!units) {
        return <ProNoDataOrRightsPage message={['no units found']} loading={loading} />;
    }

    const selectTableRow = (i: number) => {
        setSelectedUnit(units[i]);
        openUnit('view', units[i]);
        setSelectedTableRow(i);
    };

    const deleteRole = (id: string) => {
        deleteAPIRequest<Components.Schemas.CostFunctionRoleDTO>(id, UNITS_API_BASE).then(() => {
            openUnit('hidden');
            sync();
        });
    };

    const tableData: TableColData[] = [
        {
            name: t('table.cols.name'),
            cellRender: (i) =>
                i === 0 ? (
                    <>
                        <div className="ProTable__levelBars "></div>
                        <div className="ProTable__level ProTable__level--open">
                            <ProIcon icon={ProIconData.chevron_outline_down} customSize="14" />
                        </div>
                        <span className="ProTable__name">
                            {t('table.cols.summaryIn')} {portfolio?.name ?? 'Portfolio'}
                        </span>
                    </>
                ) : (
                    <>
                        <div className="ProTable__levelBars ">
                            <div className="ProTable__levelBar ProTable__levelBar-1">1</div>
                        </div>
                        <span className="ProTable__name" onClick={() => selectTableRow(i - 1)}>
                            <ProIcon size={ProSize.Small} icon={ProIconData.person_fill} />
                            {units![i - 1]?.name}
                        </span>
                    </>
                ),
            className: 'TableRowName ProTable__levels',
            colSpace: 'extend',
        },
        {
            name: t('table.cols.action'),
            cellRender: (i) =>
                i === 0 ? (
                    <>
                        <div className="ProTable--actionCell">
                            <ProButton
                                leftIcon={ProIconDataButtons.add_xs}
                                onClick={() => addUnit()}
                                tooltip={t('buttons.add', { var: 'types.inside' })}
                                mainIntent={ProIntent.Transparent}
                                hoverIntent={ProIntent.Success}
                                lineIntent={ProIntent.Success}
                                disabled={getHiddenForWindowDisplayState([unitMode])}
                                size={ProSize.XSmall}
                                text={t('buttons.add', { var: 'types.unit' })}
                                placement="bottom"
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <ActionCell
                            i={i}
                            selectTableRow={selectTableRow}
                            detailWindowMode={unitMode}
                            deleteAction={() => deleteRole(units[i - 1].id!)}
                        />
                    </>
                ),
            colSpace: 4,
        },
        {
            name: t('table.cols.description'),
            cellRender: (i) => units![i - 1]?.description ?? '',
            colSpace: 4,
            // hidden: () => !(roleIsDisplayed),
        },
        {
            name: t('table.cols.dimensionType'),
            cellRender: (i) => units![i - 1]?.dimensionType,
            colSpace: 4,
            // hidden: () => !(roleIsDisplayed),
        },
        {
            name: t('table.cols.shortName'),
            cellRender: (i) => units![i - 1]?.shortName,
            colSpace: 3,
            // hidden: () => !(roleIsDisplayed),
        },
    ];

    return (
        <ProPage
            breadCrump={getBaseBCItemForType([ApplicationElementType.PortfolioMan, ApplicationElementType.BaseData], t)}
            name={t('navigation.portfolioMan.baseData.units')}
            displayProjectSelector
        >
            <ProTable
                data={tableData}
                rowsCount={units.length + 1}
                selectedTableRow={selectedTableRow}
                getRowClass={(i) => (i < 1 ? '' : `ProTable--row__level-1`)}
            />

            <DetaiWindowWrapper hidden={[roleIsDisplayed]}>
                <UnitSummaryWindow units={units} />
                {renderUnitDetailWindow()}
            </DetaiWindowWrapper>
        </ProPage>
    );
};
